/**
 * chatHistoryService.ts
 *
 * Persists chat messages to Firestore using session-level documents:
 *   masterProfiles/{userId}/conversations/{YYYY-MM-DD_sessionId}
 *
 * Each session document contains a `messages` array, making it easy to browse
 * conversations in Firebase Console — documents sort chronologically by date
 * prefix and each document shows the full conversation at a glance.
 *
 * Backward compatibility: loadMessages falls back to the legacy per-message
 * subcollection (chatMessages/{messageId}) if no conversations exist yet.
 * New writes always go to the conversations subcollection only.
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  orderBy,
  limit,
  writeBatch,
  updateDoc,
  arrayUnion,
  increment,
} from 'firebase/firestore';
import { db } from './firebaseConfig';
import type { ChatMessage, WebSearchResultItem } from '../types/conversation';

/** Maximum messages to load on init (most recent) */
const MAX_LOAD_MESSAGES = 100;

/** Firestore-safe message shape (no Date objects) */
interface FirestoreChatMessage {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  imageUrl?: string;
  isSystemSuggestion?: boolean;
  timestamp: string;
  type?: string;
  source?: string;
  webSearchResults?: WebSearchResultItem[];
}

/**
 * Session-level document stored in conversations subcollection.
 * NOTE: Firestore has a 1 MiB document size limit. Very long conversations
 * (thousands of messages with webSearchResults) could approach this. If that
 * becomes an issue, shard messages into sub-documents per session.
 */
interface ConversationDoc {
  sessionId: string;
  startedAt: string;
  lastMessageAt: string;
  messageCount: number;
  messages: FirestoreChatMessage[];
  openAIConversationId?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Legacy per-message subcollection ref */
function legacyMessagesRef(userId: string) {
  return collection(db, 'masterProfiles', userId, 'chatMessages');
}

/** New conversations subcollection ref */
function conversationsRef(userId: string) {
  return collection(db, 'masterProfiles', userId, 'conversations');
}

/**
 * Build a stable, sortable document ID: YYYY-MM-DD_<sessionId-prefix>
 * Uses a date embedded in session-id (ISO prefix) when available,
 * so a session that spans midnight is never split across two docs.
 */
function conversationDocId(sessionId: string): string {
  // Session IDs generated by the app look like "2026-03-27T…" or a UUID.
  // Extract the date prefix from a leading ISO-like string if present.
  const isoMatch = sessionId.match(/^(\d{4}-\d{2}-\d{2})/);
  const datePrefix = isoMatch ? isoMatch[1] : new Date().toISOString().slice(0, 10);
  // Use first 8 chars of sessionId to keep doc IDs manageable
  const shortSession = sessionId.replace(/^\d{4}-\d{2}-\d{2}[T_-]?/, '').slice(0, 8) || sessionId.slice(0, 8);
  return `${datePrefix}_${shortSession}`;
}

/** Strip undefined values from an object (Firestore rejects undefined).
 *  Returns Partial<T> because the function may remove keys whose values
 *  are `undefined`, so the caller should not assume all required fields remain. */
function stripUndefined<T extends object>(obj: T): Partial<T> {
  const result = {} as Record<string, unknown>;
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      result[key] = value;
    }
  }
  return result as Partial<T>;
}

function toFirestoreMessage(msg: ChatMessage): FirestoreChatMessage {
  return {
    id: msg.id,
    sender: msg.sender,
    text: msg.text ?? '',
    ...(msg.imageUrl ? { imageUrl: msg.imageUrl } : {}),
    ...(msg.isSystemSuggestion ? { isSystemSuggestion: true } : {}),
    timestamp:
      typeof msg.timestamp === 'string'
        ? msg.timestamp
        : msg.timestamp instanceof Date
          ? msg.timestamp.toISOString()
          : new Date().toISOString(),
    ...(msg.type ? { type: msg.type } : {}),
    ...(msg.source ? { source: msg.source } : {}),
    ...(msg.webSearchResults?.length
      ? { webSearchResults: msg.webSearchResults.map(r => stripUndefined(r) as WebSearchResultItem) }
      : msg.type === 'web-search'
        ? { webSearchResults: [] }
        : {}),
  };
}

function fromFirestoreMessage(data: FirestoreChatMessage): ChatMessage {
  return {
    id: data.id,
    sender: data.sender,
    text: data.text,
    ...(data.imageUrl ? { imageUrl: data.imageUrl } : {}),
    ...(data.isSystemSuggestion ? { isSystemSuggestion: true } : {}),
    timestamp: data.timestamp,
    ...(data.type ? { type: data.type as ChatMessage['type'] } : {}),
    ...(data.source ? { source: data.source as ChatMessage['source'] } : {}),
    ...(data.webSearchResults?.length
      ? { webSearchResults: data.webSearchResults }
      : data.type === 'web-search'
        ? { webSearchResults: [] }
        : {}),
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Save a single message to the session's conversation document.
 * Uses updateDoc-first to avoid a read; falls back to setDoc on not-found.
 * arrayUnion + increment are atomic so concurrent writes never lose data.
 */
export async function saveMessage(
  userId: string,
  message: ChatMessage,
  sessionId: string,
): Promise<void> {
  const docId = conversationDocId(sessionId);
  const ref = doc(conversationsRef(userId), docId);
  const fsMsg = toFirestoreMessage(message);

  try {
    // Fast path: doc already exists — atomic append (no read needed)
    await updateDoc(ref, {
      messages: arrayUnion(fsMsg),
      lastMessageAt: fsMsg.timestamp,
      messageCount: increment(1),
    });
  } catch (err: unknown) {
    // If doc doesn't exist yet, create it
    if ((err as { code?: string }).code === 'not-found') {
      await setDoc(ref, {
        sessionId,
        startedAt: fsMsg.timestamp,
        lastMessageAt: fsMsg.timestamp,
        messageCount: 1,
        messages: [fsMsg],
      } satisfies ConversationDoc);
    } else {
      throw err;
    }
  }

  // Increment profile-level totalChatMessages counter for user messages
  if (message.sender === 'user') {
    const profileRef = doc(db, 'masterProfiles', userId);
    setDoc(profileRef, { meta: { totalChatMessages: increment(1) } }, { merge: true })
      .catch((e: unknown) => console.warn('Failed to increment totalChatMessages', e));
  }
}

/**
 * Save multiple messages in a single write.
 * Uses updateDoc-first to avoid a read; falls back to setDoc on not-found.
 */
export async function saveMessages(
  userId: string,
  msgs: ChatMessage[],
  sessionId: string,
): Promise<void> {
  if (msgs.length === 0) return;

  const docId = conversationDocId(sessionId);
  const ref = doc(conversationsRef(userId), docId);
  const fsMsgs = msgs.map(toFirestoreMessage);
  const firstTs = fsMsgs[0].timestamp;
  const lastTs = fsMsgs[fsMsgs.length - 1].timestamp;

  try {
    await updateDoc(ref, {
      messages: arrayUnion(...fsMsgs),
      lastMessageAt: lastTs,
      messageCount: increment(fsMsgs.length),
    });
  } catch (err: unknown) {
    if ((err as { code?: string }).code === 'not-found') {
      await setDoc(ref, {
        sessionId,
        startedAt: firstTs,
        lastMessageAt: lastTs,
        messageCount: fsMsgs.length,
        messages: fsMsgs,
      } satisfies ConversationDoc);
    } else {
      throw err;
    }
  }
}

/**
 * Load the most recent messages for a user, ordered by timestamp ascending.
 * Reads from conversations subcollection first; falls back to legacy chatMessages.
 */
export async function loadMessages(
  userId: string,
  maxCount: number = MAX_LOAD_MESSAGES,
): Promise<ChatMessage[]> {
  // Try new conversations subcollection first.
  // Estimate how many session docs to fetch — assume ~10 msgs per session.
  const sessionLimit = Math.max(5, Math.ceil(maxCount / 10));
  const convQuery = query(
    conversationsRef(userId),
    orderBy('lastMessageAt', 'desc'),
    limit(sessionLimit),
  );
  const convSnap = await getDocs(convQuery);

  if (!convSnap.empty) {
    // Gather all messages from recent conversations, take last maxCount
    const allMessages: ChatMessage[] = [];
    convSnap.forEach((d) => {
      const data = d.data() as ConversationDoc;
      for (const msg of data.messages) {
        allMessages.push(fromFirestoreMessage(msg));
      }
    });
    // Sort by timestamp ascending, take last maxCount
    allMessages.sort((a, b) => {
      const ta = typeof a.timestamp === 'string' ? a.timestamp : '';
      const tb = typeof b.timestamp === 'string' ? b.timestamp : '';
      return ta.localeCompare(tb);
    });
    return allMessages.slice(-maxCount);
  }

  // Fallback: legacy per-message subcollection
  // TODO(Phase 3): Remove after confirming all users have data in conversations.
  //                New writes only go to conversations; this path serves users
  //                whose chat history predates the conversations migration.
  const legacyQuery = query(
    legacyMessagesRef(userId),
    orderBy('timestamp', 'desc'),
    limit(maxCount),
  );
  const legacySnap = await getDocs(legacyQuery);
  const messages: ChatMessage[] = [];
  legacySnap.forEach((d) => {
    const data = d.data() as FirestoreChatMessage & { sessionId: string };
    messages.push(fromFirestoreMessage(data));
  });
  messages.reverse();
  return messages;
}

/**
 * Delete specific messages by ID from conversations.
 * Used for cleaning up streaming artifacts.
 * Chunks into batches of 500 to stay within Firestore batch limit.
 */
export async function deleteMessagesByIds(userId: string, ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const idSet = new Set(ids);

  // Update conversation docs by filtering out the target messages
  const convSnap = await getDocs(conversationsRef(userId));
  if (convSnap.empty) return;

  let batch = writeBatch(db);
  let opCount = 0;
  for (const d of convSnap.docs) {
    const data = d.data() as ConversationDoc;
    const filtered = data.messages.filter((m) => !idSet.has(m.id));
    if (filtered.length !== data.messages.length) {
      if (filtered.length === 0) {
        batch.delete(d.ref);
      } else {
        batch.update(d.ref, {
          messages: filtered,
          messageCount: filtered.length,
          lastMessageAt: filtered[filtered.length - 1].timestamp,
        });
      }
      opCount++;
      if (opCount === 500) {
        await batch.commit();
        batch = writeBatch(db);
        opCount = 0;
      }
    }
  }
  if (opCount > 0) {
    await batch.commit();
  }
}

/**
 * Save the OpenAI Conversations API conversation ID to the current session doc.
 * Creates the doc if it doesn't exist yet.
 */
export async function saveOpenAIConversationId(
  userId: string,
  sessionId: string,
  openAIConversationId: string,
): Promise<void> {
  const docId = conversationDocId(sessionId);
  const ref = doc(conversationsRef(userId), docId);
  try {
    await updateDoc(ref, { openAIConversationId });
  } catch (err: unknown) {
    if ((err as { code?: string }).code === 'not-found') {
      // Use merge to avoid overwriting messages if a concurrent saveMessage created the doc
      await setDoc(ref, { openAIConversationId }, { merge: true });
    } else {
      throw err;
    }
  }
}

/**
 * Load the OpenAI conversation ID for a specific session.
 * Uses the same doc ID derivation as saveOpenAIConversationId for consistency.
 */
export async function loadOpenAIConversationId(
  userId: string,
  sessionId: string,
): Promise<string | undefined> {
  const docId = conversationDocId(sessionId);
  const ref = doc(conversationsRef(userId), docId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return undefined;
  const data = snap.data() as ConversationDoc;
  return data.openAIConversationId;
}

export async function clearMessages(userId: string): Promise<void> {
  // Delete new conversations subcollection
  const convSnap = await getDocs(conversationsRef(userId));
  if (!convSnap.empty) {
    let batch = writeBatch(db);
    let count = 0;
    for (const d of convSnap.docs) {
      batch.delete(d.ref);
      count++;
      if (count === 500) {
        await batch.commit();
        batch = writeBatch(db);
        count = 0;
      }
    }
    if (count > 0) {
      await batch.commit();
    }
  }

  // Also clean up legacy chatMessages subcollection
  const legacySnap = await getDocs(legacyMessagesRef(userId));
  if (!legacySnap.empty) {
    let batch = writeBatch(db);
    let count = 0;
    for (const d of legacySnap.docs) {
      batch.delete(d.ref);
      count++;
      if (count === 500) {
        await batch.commit();
        batch = writeBatch(db);
        count = 0;
      }
    }
    if (count > 0) {
      await batch.commit();
    }
  }
}
